import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, Menu, X, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('idle');

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts/${slug}`);
      setPost(response.data.post);
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setSubscribeStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/blog/newsletter/subscribe`, { email });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus('idle'), 5000);
    } catch (error) {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus('idle'), 5000);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="home-container">
        <div className="blog-loading" style={{ padding: '200px 20px', textAlign: 'center' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="home-container">
        <header className="dark-header">
          <div className="logo-container">
            <Link to="/" className="logo-text" style={{ textDecoration: 'none' }}>WestBay</Link>
          </div>
          <nav className="dark-nav">
            <button onClick={() => navigate('/blog')} className="dark-nav-link">← Back to Blog</button>
          </nav>
        </header>
        <div className="blog-empty" style={{ padding: '200px 20px', textAlign: 'center' }}>
          <h3>Post not found</h3>
          <Link to="/blog" className="read-more-btn">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      {/* Header */}
      <header className="dark-header">
        <div className="logo-container">
          <Link to="/" className="logo-text" style={{ textDecoration: 'none' }}>WestBay</Link>
        </div>
        
        <nav className="dark-nav">
          <button onClick={() => navigate('/')} className="dark-nav-link">Home</button>
          <button onClick={() => navigate('/#services')} className="dark-nav-link">Services</button>
          <button onClick={() => navigate('/#poser-ai')} className="dark-nav-link">Poser AI™</button>
          <button onClick={() => navigate('/blog')} className="dark-nav-link" style={{ background: '#FF6B35', color: '#fff' }}>Blog</button>
        </nav>

        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      {mobileMenuOpen && (
        <div className="mobile-menu">
          <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="mobile-nav-link">Home</button>
          <button onClick={() => { navigate('/#services'); setMobileMenuOpen(false); }} className="mobile-nav-link">Services</button>
          <button onClick={() => { navigate('/#poser-ai'); setMobileMenuOpen(false); }} className="mobile-nav-link">Poser AI™</button>
          <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="mobile-nav-link" style={{ background: '#FF6B35', color: '#fff' }}>Blog</button>
        </div>
      )}

      {/* Article */}
      <article className="blog-article-section">
        <div className="blog-article-container">
          {/* Header */}
          <div className="article-header-pop">
            <div className="article-meta-pop">
              <span><Calendar size={16} /> {formatDate(post.created_at)}</span>
              <span><User size={16} /> {post.author}</span>
            </div>
            <h1>{post.title}</h1>
            <div className="article-tags-pop">
              {post.tags.map(tag => (
                <span key={tag} className="article-tag-chip">
                  <Tag size={14} /> {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Featured Image */}
          {post.featured_image && (
            <div className="article-featured-image">
              <img src={post.featured_image} alt={post.title} />
            </div>
          )}

          {/* Content */}
          <div 
            className="article-body-pop"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Newsletter CTA */}
          <div className="article-newsletter-pop">
            <h3>Enjoyed this article?</h3>
            <p>Subscribe to get the latest insights delivered to your inbox.</p>
            {subscribeStatus === 'success' ? (
              <div className="subscribe-success">Thanks for subscribing!</div>
            ) : (
              <form onSubmit={handleSubscribe} className="subscribe-form-inline">
                <div className="subscribe-input">
                  <Mail size={18} />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" disabled={subscribeStatus === 'loading'}>
                  Subscribe
                </button>
              </form>
            )}
          </div>

          {/* Back Link */}
          <div className="article-back-link">
            <Link to="/blog">← Back to Blog</Link>
          </div>
        </div>
      </article>

      {/* Floating Contact Button */}
      <button 
        className="floating-chat-button" 
        onClick={() => navigate('/#contact')}
        aria-label="Contact Us"
      >
        <Mail size={24} />
        <span className="floating-button-text">Contact</span>
      </button>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-single-line">
          <div className="footer-brand">
            <div className="logo-container">
              <span className="logo-text">WestBay</span>
            </div>
          </div>
          <div className="footer-center">
            <p className="body-small">© 2026 WestBay Group LLC. All rights reserved.</p>
            <span className="footer-separator">|</span>
            <p className="footer-patent"><span className="patent-text">Patent Pending</span></p>
          </div>
          <div className="footer-links">
            <Link to="/privacy-policy" className="footer-link">Privacy</Link>
            <Link to="/terms-of-service" className="footer-link">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostView;
